import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../config";
import { parseSchedule } from "../../components/SchedulePicker";
import { syLabel } from "../../utils/schoolYear";
import s from "./MyGradingPage.module.css";
import a from "./MyAttendancePage.module.css";

/* Teacher → Attendance hub. One card per class in the active term with
 * today's attendance status, today's classes first. "Take attendance"
 * opens the existing per-class page (/teacher/attendance/:id). */

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
/** Local-time YYYY-MM-DD (same as the attendance page). */
function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toMinutes(t) {
  const m = (t || "").match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
function fmtTime(t) {
  const m = toMinutes(t);
  if (m == null) return "";
  const h = Math.floor(m / 60);
  return `${((h + 11) % 12) + 1}:${String(m % 60).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}
function hueFor(seed) {
  const str = String(seed || "•");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}
function abbrev(src) {
  const v = (src || "").toString();
  const words = v.replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return v.replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "S";
}
const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MyAttendancePage() {
  const user = getUser();
  const teacherStaffId = user?.linkedEntity?.id;
  const navigate = useNavigate();
  const today = isoToday();
  const todayKey = DAY_KEYS[new Date().getDay()];

  const [data, setData] = useState({
    loading: true, error: "",
    offerings: [], subjects: {}, sections: {}, gradeLevels: {}, rooms: {},
    enrollments: [], activeSy: null, activeSem: null,
  });
  const [todayRows, setTodayRows] = useState({}); // offeringId -> attendance rows for today
  const [scope, setScope] = useState("today");    // "today" | "all"

  /* ── Load the teacher's classes + lookups ── */
  useEffect(() => {
    if (!teacherStaffId) { setData(d => ({ ...d, loading: false })); return undefined; }
    let cancelled = false;
    const safe = (u) => fetch(u).then(r => (r.ok ? r.json() : [])).catch(() => []);
    Promise.all([
      safe(`${API_BASE}/api/class-offerings?teacherStaffId=${teacherStaffId}`),
      safe(`${API_BASE}/api/subjects`),
      safe(`${API_BASE}/api/sections`),
      safe(`${API_BASE}/api/semesters`),
      safe(`${API_BASE}/api/school-years`),
      safe(`${API_BASE}/api/grade-levels`),
      safe(`${API_BASE}/api/rooms`),
      safe(`${API_BASE}/api/enrollments?teacherStaffId=${teacherStaffId}`),
    ]).then(([offerings, subjects, sections, semesters, schoolYears, gradeLevels, rooms, enrollments]) => {
      if (cancelled) return;
      const byId = (rows) => Object.fromEntries((Array.isArray(rows) ? rows : []).map(r => [r.id, r]));
      const activeSy  = schoolYears.find(y => y.active) || schoolYears[0] || null;
      const activeSem = semesters.find(sm => sm.active)
        || (activeSy ? semesters.find(sm => sm.schoolYearId === activeSy.id) : null)
        || semesters[0] || null;
      setData({
        loading: false, error: "",
        offerings: (Array.isArray(offerings) ? offerings : [])
          .filter(o => !activeSem || o.semesterId === activeSem.id),
        subjects: byId(subjects), sections: byId(sections),
        gradeLevels: byId(gradeLevels), rooms: byId(rooms),
        enrollments: Array.isArray(enrollments) ? enrollments : [],
        activeSy, activeSem,
      });
    }).catch(e => { if (!cancelled) setData(d => ({ ...d, loading: false, error: e.message || "Failed to load" })); });
    return () => { cancelled = true; };
  }, [teacherStaffId]);

  /* ── Today's attendance per class ── */
  const offeringIdsKey = data.offerings.map(o => o.id).sort().join(",");
  useEffect(() => {
    if (!offeringIdsKey) { setTodayRows({}); return undefined; }
    let cancelled = false;
    Promise.all(offeringIdsKey.split(",").map(id =>
      fetch(`${API_BASE}/api/attendance?classOfferingId=${id}&date=${today}`)
        .then(r => (r.ok ? r.json() : [])).catch(() => [])
        .then(rows => [id, Array.isArray(rows) ? rows : []])
    )).then(pairs => { if (!cancelled) setTodayRows(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [offeringIdsKey, today]);

  /** Enrolled head-count per section for the active term. */
  const rosterBySection = useMemo(() => {
    const m = new Map();
    for (const e of data.enrollments) {
      if (String(e.status || "ENROLLED").toUpperCase() !== "ENROLLED") continue;
      if (data.activeSem && e.semesterId != null && String(e.semesterId) !== String(data.activeSem.id)) continue;
      m.set(String(e.sectionId), (m.get(String(e.sectionId)) || 0) + 1);
    }
    return m;
  }, [data.enrollments, data.activeSem]);

  const cards = useMemo(() => {
    const out = data.offerings.map(o => {
      const parsed = parseSchedule(o.schedule || "");
      const meetsToday = parsed.days.includes(todayKey);
      const rows = todayRows[o.id];
      const counts = { PRESENT: 0, LATE: 0, EXCUSED: 0, ABSENT: 0 };
      for (const r of rows || []) if (counts[r.status] != null) counts[r.status]++;
      const marked = (rows || []).length;
      const sec = data.sections[o.sectionId];
      return {
        o, parsed, meetsToday, counts, marked,
        loaded: rows !== undefined,
        roster: rosterBySection.get(String(o.sectionId)) || 0,
        subject: data.subjects[o.subjectId],
        section: sec,
        grade: sec ? data.gradeLevels[sec.gradeLevelId] : null,
        room: (o.roomId != null && data.rooms[o.roomId]?.name) || o.room || "",
        start: toMinutes(parsed.start),
      };
    });
    // Today's classes first (by start time), then the rest by subject.
    out.sort((x, y) => {
      if (x.meetsToday !== y.meetsToday) return x.meetsToday ? -1 : 1;
      if (x.meetsToday && x.start != null && y.start != null && x.start !== y.start) return x.start - y.start;
      return (x.subject?.title || "").localeCompare(y.subject?.title || "");
    });
    return out;
  }, [data, todayRows, todayKey, rosterBySection]);

  const todays = cards.filter(c => c.meetsToday);
  const shown  = scope === "today" ? todays : cards;
  const doneToday = todays.filter(c => c.marked > 0).length;
  const absentToday = todays.reduce((n, c) => n + c.counts.ABSENT, 0);

  const prettyToday = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const termLabel = data.activeSy && data.activeSem
    ? `${syLabel(data.activeSy.label)} · ${data.activeSem.label} sem` : "";

  if (!teacherStaffId) {
    return (
      <div className={s.page}>
        <div className={s.emptyPremium}>
          <div className={s.emptyPremiumTitle}>Your account isn't linked to a Staff record yet.</div>
          <div className={s.emptyPremiumSub}>Ask an admin to link your user to your employee record.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <header className={s.hero}>
        <div className={s.heroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Teacher · Attendance</div>
          <h1 className={s.heroTitle}>{prettyToday}</h1>
          <p className={s.heroSub}>
            {data.loading
              ? "Loading your classes…"
              : todays.length === 0
              ? "You have no classes scheduled today."
              : <>You have <strong>{todays.length}</strong> class{todays.length === 1 ? "" : "es"} today —{" "}
                  <strong>{doneToday}</strong> with attendance taken.</>}
          </p>
        </div>
        {termLabel && <div className={s.heroTerm} title="Active term">{termLabel}</div>}
      </header>

      <div className={s.statRow}>
        <Stat label="Today's classes" value={data.loading ? "…" : todays.length} hint={`of ${cards.length} this term`} />
        <Stat label="Taken" value={data.loading ? "…" : doneToday}
              hint={todays.length ? `${todays.length - doneToday} still to take` : "—"}
              tone={todays.length && doneToday === todays.length ? "good" : "warn"} />
        <Stat label="Absent today" value={data.loading ? "…" : absentToday}
              hint="across today's classes" tone={absentToday > 0 ? "danger" : "good"} />
      </div>

      <div className={s.filterBar}>
        <div className={s.scopeTabs} role="tablist" aria-label="Which classes">
          <button type="button" role="tab" aria-selected={scope === "today"}
                  className={`${s.scopeTab} ${scope === "today" ? s.scopeTabActive : ""}`}
                  onClick={() => setScope("today")}>
            Today
          </button>
          <button type="button" role="tab" aria-selected={scope === "all"}
                  className={`${s.scopeTab} ${scope === "all" ? s.scopeTabActive : ""}`}
                  onClick={() => setScope("all")}>
            All my classes
          </button>
        </div>
      </div>

      {data.loading && (
        <div className={s.skel}><div className={s.skelCard} /><div className={s.skelCard} /><div className={s.skelCard} /></div>
      )}
      {!data.loading && data.error && <div className={s.error}>{data.error}</div>}

      {!data.loading && !data.error && shown.length === 0 && (
        <div className={s.emptyPremium}>
          <div className={s.emptyPremiumTitle}>
            {scope === "today" ? "No classes scheduled today." : "No classes this term."}
          </div>
          <div className={s.emptyPremiumSub}>
            {scope === "today" && cards.length > 0
              ? <>Switch to <strong>All my classes</strong> to take attendance for any class.</>
              : "Once the registrar assigns classes to you, they'll appear here."}
          </div>
        </div>
      )}

      {!data.loading && !data.error && shown.length > 0 && (
        <div className={s.grid}>
          {shown.map((c, idx) => {
            const label = c.subject?.code || c.subject?.title || `S${c.o.subjectId}`;
            const status = !c.loaded
              ? { tone: "info", label: "Checking…" }
              : c.marked > 0
              ? { tone: "good", label: "Taken today" }
              : c.meetsToday
              ? { tone: "danger", label: "Not taken yet" }
              : { tone: "info", label: "Not today" };
            return (
              <article key={c.o.id} className={s.card}
                       style={{ "--gHue": hueFor(label), animationDelay: `${Math.min(idx, 8) * 45}ms` }}>
                <header className={s.cardHead}>
                  <div className={s.cardIcon} aria-hidden="true">{abbrev(label)}</div>
                  <div className={s.cardIdent}>
                    <div className={s.cardTitle} title={c.subject?.title}>
                      {c.subject?.title || `Subject #${c.o.subjectId}`}
                    </div>
                    {c.subject?.code && <div className={s.cardCode}>{c.subject.code}</div>}
                  </div>
                  <span className={`${s.statusPill} ${s["statusPill_" + status.tone]}`}>{status.label}</span>
                </header>

                <div className={s.factRow}>
                  {c.section && (
                    <span className={s.factPill}>
                      {c.grade ? `${c.grade.name} · ${c.section.name}` : c.section.name}
                    </span>
                  )}
                  {c.meetsToday && c.parsed.start && (
                    <span className={`${s.factPill} ${a.timePill}`}>
                      Today {fmtTime(c.parsed.start)}–{fmtTime(c.parsed.end)}
                    </span>
                  )}
                  {!c.meetsToday && c.o.schedule && <span className={s.factPill}>{c.o.schedule}</span>}
                  {c.room && <span className={s.factPill}>Room {c.room}</span>}
                </div>

                {c.marked > 0 ? (
                  <div className={a.tally}>
                    <Tally label="Present" n={c.counts.PRESENT} tone="present" />
                    <Tally label="Late"    n={c.counts.LATE}    tone="late" />
                    <Tally label="Excused" n={c.counts.EXCUSED} tone="excused" />
                    <Tally label="Absent"  n={c.counts.ABSENT}  tone="absent" />
                    {c.roster > c.marked && (
                      <span className={a.unmarked}>{c.roster - c.marked} unmarked</span>
                    )}
                  </div>
                ) : (
                  <div className={a.notYet}>
                    {c.roster} student{c.roster === 1 ? "" : "s"} · no attendance recorded today
                  </div>
                )}

                <button type="button" className={s.cta}
                        onClick={() => navigate(`/teacher/attendance/${c.o.id}`)}>
                  ✓ {c.marked > 0 ? "Update attendance" : "Take attendance"}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div className={`${s.statTile} ${tone ? s["statTile_" + tone] : ""}`}>
      <div className={s.statLabel}>{label}</div>
      <div className={s.statValue}>{value}</div>
      <div className={s.statHint} title={hint}>{hint}</div>
    </div>
  );
}

function Tally({ label, n, tone }) {
  return (
    <span className={`${a.tallyItem} ${a["tally_" + tone]}`}>
      <strong>{n}</strong> {label}
    </span>
  );
}
